import React, { useState, useEffect, useRef, useCallback } from 'react';
import './chatwoot.css';

function formatTime(timestamp) {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const diff = Math.floor((Date.now() - date) / 60000);
  if (diff < 1) return 'agora';
  if (diff < 60) return `${diff}min`;
  if (diff < 1440) return `${Math.floor(diff / 60)}h`;
  return `${Math.floor(diff / 1440)}d`;
}

function initials(name = '') {
  return name.split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase();
}

// ── Vista de lista ────────────────────────────────────────────────────────────

function ConversationItem({ conv, onSelect }) {
  const contact = conv.meta?.sender;
  const unread = (conv.unread_count || 0) > 0;
  const preview = conv.last_non_activity_message?.content || '';

  return (
    <div
      className={`cw-item${unread ? ' unread' : ''}`}
      onClick={() => onSelect(conv)}
    >
      <div className="cw-avatar">{initials(contact?.name)}</div>
      <div className="cw-item-body">
        <div className="cw-item-top">
          <span className="cw-item-name">{contact?.name || 'Desconhecido'}</span>
          <span className="cw-item-time">{formatTime(conv.created_at)}</span>
        </div>
        <div className="cw-item-preview">
          {preview.substring(0, 60)}{preview.length > 60 ? '...' : ''}
        </div>
      </div>
      {unread && <div className="cw-unread-dot" />}
    </div>
  );
}

// ── Vista de detalhe da conversa ──────────────────────────────────────────────

function MessageBubble({ msg }) {
  const isOutgoing = msg.message_type === 1;
  return (
    <div className={`cw-msg${isOutgoing ? ' outgoing' : ' incoming'}`}>
      <div className="cw-bubble">{msg.content}</div>
      <div className="cw-msg-time">{formatTime(msg.created_at)}</div>
    </div>
  );
}

function TransferPanel({ onClose, onSelectAgent, onSelectTeam, fetchAgents, fetchAllTeams, onBack }) {
  const [agents, setAgents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // { type: 'agent'|'team', id, name }
  const [transferring, setTransferring] = useState(false);

  useEffect(() => {
    Promise.all([fetchAgents(), fetchAllTeams()])
      .then(([ag, tm]) => { setAgents(ag); setTeams(tm); })
      .finally(() => setLoading(false));
  }, [fetchAgents, fetchAllTeams]);

  async function handleConfirm() {
    setTransferring(true);
    try {
      if (pending.type === 'agent') await onSelectAgent(pending.id);
      else await onSelectTeam(pending.id);
      onClose();
      onBack();
    } finally {
      setTransferring(false);
    }
  }

  if (pending) {
    const isTeam = pending.type === 'team';
    return (
      <div className="cw-confirm-overlay" onClick={() => setPending(null)}>
        <div className="cw-confirm-dialog" onClick={e => e.stopPropagation()}>
          <div className="cw-confirm-icon" style={{ color: '#86A6AC' }}>
            <i className={`bi bi-${isTeam ? 'people-fill' : 'person-fill'}`} />
          </div>
          <div className="cw-confirm-title">Transferir conversa</div>
          <div className="cw-confirm-msg">
            Transferir para <strong>{pending.name}</strong>?
          </div>
          <div className="cw-confirm-actions">
            <button
              className="cw-confirm-cancel"
              onClick={() => setPending(null)}
              disabled={transferring}
            >
              Cancelar
            </button>
            <button
              className="cw-confirm-ok"
              onClick={handleConfirm}
              disabled={transferring}
            >
              {transferring
                ? <i className="bi bi-hourglass-split" />
                : <><i className="bi bi-arrow-left-right me-1" />Transferir</>
              }
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = search.toLowerCase();
  const filteredAgents = agents.filter(a => a.name?.toLowerCase().includes(q));
  const filteredTeams = teams.filter(t => t.name?.toLowerCase().includes(q));

  return (
    <div className="cw-confirm-overlay" onClick={onClose}>
      <div className="cw-transfer-panel" onClick={e => e.stopPropagation()}>
        <div className="cw-transfer-header">
          <span>Transferir conversa</span>
          <button className="cw-transfer-close" onClick={onClose}>
            <i className="bi bi-x-lg" />
          </button>
        </div>
        <input
          className="cw-transfer-search"
          placeholder="Buscar agente ou time..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoFocus
        />
        {loading ? (
          <div className="cw-loading" style={{ padding: '1rem' }}>
            <i className="bi bi-hourglass-split me-2" />Carregando...
          </div>
        ) : (
          <div className="cw-transfer-list">
            {filteredAgents.length > 0 && (
              <>
                <div className="cw-transfer-section">Agentes</div>
                {filteredAgents.map(agent => (
                  <button
                    key={agent.id}
                    className="cw-transfer-item"
                    onClick={() => setPending({ type: 'agent', id: agent.id, name: agent.name })}
                  >
                    <div className="cw-avatar" style={{ width: 28, height: 28, fontSize: 10, flexShrink: 0 }}>
                      {(agent.name || '').split(' ').slice(0, 2).map(w => w[0] || '').join('').toUpperCase()}
                    </div>
                    <span>{agent.name}</span>
                  </button>
                ))}
              </>
            )}
            {filteredTeams.length > 0 && (
              <>
                <div className="cw-transfer-section">Times</div>
                {filteredTeams.map(team => (
                  <button
                    key={team.id}
                    className="cw-transfer-item"
                    onClick={() => setPending({ type: 'team', id: team.id, name: team.name })}
                  >
                    <div className="cw-transfer-team-icon">
                      <i className="bi bi-people-fill" />
                    </div>
                    <span>{team.name}</span>
                  </button>
                ))}
              </>
            )}
            {filteredAgents.length === 0 && filteredTeams.length === 0 && (
              <div className="cw-empty" style={{ padding: '1rem' }}>
                {search
                  ? 'Nenhum resultado para a busca'
                  : <><i className="bi bi-moon-stars me-2" />Nenhum agente online no momento</>
                }
              </div>
            )}
            {filteredAgents.length === 0 && filteredTeams.length > 0 && !search && (
              <div style={{ padding: '6px 14px 2px', fontSize: '0.75rem', color: '#9ca3af' }}>
                <i className="bi bi-info-circle me-1" />Nenhum agente online — transferindo para time atribui à fila
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationDetail({ conv, messages, isLoading, onBack, onSend, onResolve, initialUnreadCount, fetchAgents, fetchAllTeams, onAssignAgent, onAssignTeam }) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  // Índice do primeiro mensagem não lida (para mostrar divisor)
  let unreadStartIdx = -1;
  if (initialUnreadCount > 0 && messages.length > 0) {
    let count = 0;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].message_type === 0) {
        count++;
        if (count === initialUnreadCount) { unreadStartIdx = i; break; }
      }
    }
    if (unreadStartIdx === -1) unreadStartIdx = messages.findIndex(m => m.message_type === 0);
  }

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [conv]);

  async function handleSend() {
    if (!text.trim() || sending) return;
    setSending(true);
    try {
      await onSend(text.trim());
      setText('');
    } finally {
      setSending(false);
    }
  }

  async function handleResolveConfirmed() {
    if (resolving) return;
    setResolving(true);
    try {
      await onResolve(conv.id);
      onBack();
    } finally {
      setResolving(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  const contact = conv.meta?.sender;
  const isResolved = conv.status === 'resolved';

  return (
    <>
      <div className="cw-detail-header">
        <button className="cw-back-btn" onClick={onBack} title="Voltar">
          <i className="bi bi-arrow-left" />
        </button>
        <div className="cw-avatar" style={{ width: 32, height: 32, fontSize: 11 }}>
          {initials(contact?.name)}
        </div>
        <div className="cw-detail-contact">
          <div className="cw-detail-name">{contact?.name || 'Desconhecido'}</div>
          <div className="cw-detail-id">Conversa #{conv.id}</div>
        </div>
      </div>

      {!isResolved && (
        <div className="cw-detail-actions">
          <button
            className="cw-action-btn cw-action-transfer"
            onClick={() => setShowTransfer(true)}
          >
            <i className="bi bi-arrow-left-right" /> Transferir
          </button>
          <button
            className="cw-action-btn cw-action-resolve"
            onClick={() => setShowConfirm(true)}
            disabled={resolving}
          >
            {resolving
              ? <i className="bi bi-hourglass-split" />
              : <><i className="bi bi-check-circle" /> Encerrar</>
            }
          </button>
        </div>
      )}

      {showTransfer && (
        <TransferPanel
          onClose={() => setShowTransfer(false)}
          onSelectAgent={id => onAssignAgent(conv.id, id)}
          onSelectTeam={id => onAssignTeam(conv.id, id)}
          fetchAgents={fetchAgents}
          fetchAllTeams={fetchAllTeams}
          onBack={onBack}
        />
      )}

      {showConfirm && (
        <div className="cw-confirm-overlay" onClick={() => setShowConfirm(false)}>
          <div className="cw-confirm-dialog" onClick={e => e.stopPropagation()}>
            <div className="cw-confirm-icon"><i className="bi bi-check-circle-fill" /></div>
            <div className="cw-confirm-title">Encerrar conversa</div>
            <div className="cw-confirm-msg">
              Confirma o encerramento da conversa com{' '}
              <strong>{contact?.name || 'este contato'}</strong>?
            </div>
            <div className="cw-confirm-actions">
              <button className="cw-confirm-cancel" onClick={() => setShowConfirm(false)}>
                Cancelar
              </button>
              <button
                className="cw-confirm-ok"
                onClick={() => { setShowConfirm(false); handleResolveConfirmed(); }}
              >
                <i className="bi bi-check-lg me-1" />Encerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="cw-messages">
        {isLoading ? (
          <div className="cw-loading">
            <i className="bi bi-hourglass-split me-2" />
            Carregando mensagens...
          </div>
        ) : messages.length === 0 ? (
          <div className="cw-empty">Nenhuma mensagem</div>
        ) : (
          messages.map((msg, i) => (
            <React.Fragment key={msg.id}>
              {i === unreadStartIdx && (
                <div className="cw-unread-divider">
                  <span>
                    {initialUnreadCount} não lida{initialUnreadCount > 1 ? 's' : ''}
                  </span>
                </div>
              )}
              <MessageBubble msg={msg} />
            </React.Fragment>
          ))
        )}
        <div ref={bottomRef} />
      </div>

      {!isResolved && (
        <div className="cw-reply">
          <textarea
            ref={inputRef}
            className="cw-reply-input"
            placeholder="Digite sua resposta... (Enter para enviar)"
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            rows={2}
            disabled={sending}
          />
          <button
            className="cw-reply-send"
            onClick={handleSend}
            disabled={!text.trim() || sending}
            title="Enviar"
          >
            {sending
              ? <i className="bi bi-hourglass-split" />
              : <i className="bi bi-send-fill" />
            }
          </button>
        </div>
      )}
    </>
  );
}

// ── Configuração de status ────────────────────────────────────────────────────

const STATUS_CONFIG = {
  online:  { label: 'Online',  dotClass: 'online'  },
  busy:    { label: 'Ausente', dotClass: 'busy'    },
  offline: { label: 'Offline', dotClass: 'offline' },
};

// ── Componente principal ──────────────────────────────────────────────────────

const TABS = [
  { key: 'mine', label: 'Minhas Conversas' },
  { key: 'team', label: 'Meu Time' },
  // { key: 'resolved', label: 'Resolvidas' }, // reservado para uso futuro
];

export default function ChatwootOffcanvas({
  isOpen,
  onClose,
  conversations,
  isLoading,
  onRefresh,
  fetchMessages,
  sendMessage,
  resolveConversation,
  markAsRead,
  fetchAgents,
  fetchAllTeams,
  assignAgent,
  assignTeam,
  openConversationId,
  chatwootBase,
  accountId,
  agentAvailability,
  onSetAvailability,
  liveConvUpdate,
}) {
  const [tab, setTab] = useState('mine');
  const [activeConv, setActiveConv] = useState(null);
  const [initialUnread, setInitialUnread] = useState(0);
  const [messages, setMessages] = useState([]);
  const [msgLoading, setMsgLoading] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const lastMsgIdRef = useRef(null);

  const openConv = useCallback(async (conv) => {
    const unread = conv.unread_count || 0;
    setActiveConv(conv);
    setInitialUnread(unread);
    setMessages([]);
    setMsgLoading(true);
    lastMsgIdRef.current = conv.last_non_activity_message?.id || null;
    try {
      const msgs = await fetchMessages(conv.id);
      setMessages(msgs);
      if (unread > 0) markAsRead(conv.id);
    } catch (e) {
      console.warn('[Chatwoot] Erro ao carregar mensagens:', e);
    } finally {
      setMsgLoading(false);
    }
  }, [fetchMessages, markAsRead]);

  // Navega para conversa específica quando vem do toast — não inclui `conversations`
  // para evitar que o polling (a cada 15s) resete a conversa aberta
  useEffect(() => {
    if (!openConversationId || !isOpen) return;
    const all = [
      ...(conversations.mine || []),
      ...(conversations.team || []),
      // ...(conversations.resolved || []), // reservado para uso futuro
    ];
    const conv = all.find(c => c.id === openConversationId);
    if (conv) openConv(conv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openConversationId, isOpen]);

  // SSE → liveConvUpdate → busca mensagens diretamente (sem depender de conversations)
  useEffect(() => {
    if (!activeConv || !liveConvUpdate) return;
    if (liveConvUpdate.conversationId !== activeConv.id) return;
    fetchMessages(activeConv.id)
      .then(msgs => { if (msgs.length > 0) setMessages(msgs); })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveConvUpdate]);

  function handleBack() {
    setActiveConv(null);
    setMessages([]);
  }

  function handleClose() {
    setActiveConv(null);
    setMessages([]);
    onClose();
  }

  async function handleSend(content) {
    if (!activeConv) return;
    await sendMessage(activeConv.id, content);
    const updated = await fetchMessages(activeConv.id);
    setMessages(updated);
  }

  async function handleResolve(conversationId) {
    await resolveConversation(conversationId);
  }

  const userData = (() => {
    try { return JSON.parse(localStorage.getItem('userData') || '{}'); }
    catch { return {}; }
  })();

  const items = conversations[tab] || [];
  const unreadMine = (conversations.mine || []).filter(c => c.unread_count > 0).length;

  return (
    <>
      <div
        className={`cw-overlay${isOpen ? ' active' : ''}`}
        onClick={handleClose}
      />
      <div className={`cw-panel${isOpen ? ' active' : ''}`} role="dialog" aria-label="Conversas">

        {activeConv ? (
          <ConversationDetail
            conv={activeConv}
            messages={messages}
            isLoading={msgLoading}
            onBack={handleBack}
            onSend={handleSend}
            onResolve={handleResolve}
            initialUnreadCount={initialUnread}
            fetchAgents={fetchAgents}
            fetchAllTeams={fetchAllTeams}
            onAssignAgent={assignAgent}
            onAssignTeam={assignTeam}
          />
        ) : (
          <>
            <div className="cw-header">
              <i className="bi bi-chat-dots" style={{ fontSize: '1.1rem' }} />
              <span className="cw-header-title">Conversas</span>
              {unreadMine > 0 && (
                <span className="cw-header-badge">{unreadMine}</span>
              )}
              <button onClick={onRefresh} title="Atualizar">
                <i className={`bi bi-arrow-clockwise${isLoading ? ' spin' : ''}`} />
              </button>
              <button onClick={handleClose} aria-label="Fechar">
                <i className="bi bi-x-lg" />
              </button>
            </div>

            <div className="cw-tabs">
              {TABS.map(({ key, label }) => {
                const count = conversations[key]?.length || 0;
                const hasUnread = key === 'mine'
                  ? (conversations.mine || []).filter(c => c.unread_count > 0).length
                  : 0;
                return (
                  <button
                    key={key}
                    className={`cw-tab${tab === key ? ' active' : ''}`}
                    onClick={() => setTab(key)}
                  >
                    {label}
                    {hasUnread > 0 && (
                      <span className="cw-tab-badge">{hasUnread}</span>
                    )}
                    {hasUnread === 0 && count > 0 && (
                      <span style={{ marginLeft: 4, fontSize: '0.72rem', opacity: 0.55 }}>
                        ({count})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="cw-list">
              {isLoading ? (
                <div className="cw-loading">
                  <i className="bi bi-hourglass-split me-2" />
                  Carregando...
                </div>
              ) : items.length === 0 ? (
                <div className="cw-empty">
                  <i className="bi bi-check2-all" style={{ fontSize: '1.5rem', display: 'block', marginBottom: 6 }} />
                  Nenhuma conversa
                </div>
              ) : (
                items.map(conv => (
                  <ConversationItem key={conv.id} conv={conv} onSelect={openConv} />
                ))
              )}
            </div>

            <div className="cw-footer">
              {/* Seletor de status */}
              <div
                className="cw-status-selector"
                onClick={() => setShowStatusMenu(s => !s)}
                title="Alterar status"
              >
                <span className={`cw-dot ${(STATUS_CONFIG[agentAvailability] || STATUS_CONFIG.offline).dotClass}`} />
                <span className="cw-status-label">
                  {(STATUS_CONFIG[agentAvailability] || STATUS_CONFIG.offline).label}
                </span>
                <i className="bi bi-chevron-up cw-status-chevron" />
              </div>
              {showStatusMenu && (
                <>
                  <div className="cw-status-backdrop" onClick={() => setShowStatusMenu(false)} />
                  <div className="cw-status-menu">
                    {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                      <button
                        key={key}
                        className={`cw-status-option${agentAvailability === key ? ' active' : ''}`}
                        onClick={() => { onSetAvailability?.(key); setShowStatusMenu(false); }}
                      >
                        <span className={`cw-dot ${cfg.dotClass}`} />
                        {cfg.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
              <span className="cw-user-label">{userData.nome || userData.name || 'Agente'}</span>
            </div>
          </>
        )}
      </div>
    </>
  );
}
